import React, { useCallback, useMemo, useState, useEffect } from 'react'
import DropdownMenu, { DropdownItem } from '../../../DropdownMenu'
import AddDispatchOrganization from '../../../AddDispatchOrganization'
import GoogleMaps, { Marker } from '../../../GoogleMaps'
import Button from '../../../Button'
import Info from '../../../Info'
import UserDetails from '../../../UserDetails'
import DateTime from '../../../DateTime'
import ModalDialog from '../../../ModalDialog'
import TextField from '../../../Form/TextField'
import { IconPickerItem } from '../../../Form/IconField'
import { formatAddress } from '../../../LocationRenderer'
import { fetchUserProfile } from '../../../../hooks/userProfiles'
import { useNotifications, useForm, useApiRequest } from '../../../../hooks'
import { getUserAvatar } from '../../../../hooks/displayNameHelper'
import { useDispatchData } from '../../DispatchDataWrapper'
import styles from './styles.module.scss'
import {
  doc,
  getFirestore,
  updateDoc,
  arrayUnion,
  arrayRemove,
  getDoc,
  setDoc,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore'
import { db } from '../../../../firebase'
import { CircleX, Loader } from 'lucide-react'
import Spinner from '../../../Spinner'
import { sendNotificationToTopic } from '../../../../hooks/notificationService'
import { useMap } from '../../../../hooks/MapContext'

function UserRow({ user, timestamp, openDetails }) {
  const [fetchedUser, setFetchedUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const userId = useMemo(() => {
    return user?.createdBy || user?.userId || null
  }, [user])

  useEffect(() => {
    if (!userId) return
    let isMounted = true

    const fetchUserData = async () => {
      try {
        setLoading(true)
        // Cross-organisation responders and incident creators land here, so the
        // read is legitimately denied and getUserProfiles answers instead.
        const userData = await fetchUserProfile(userId)
        if (userData) {
          setFetchedUser(userData)
        }
      } catch (err) {
        console.error('Error fetching user data for UserRow:', err)
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    fetchUserData()
    return () => {
      isMounted = false
    }
  }, [userId])

  const displayName = useMemo(() => {
    if (loading) return <Spinner />
    if (fetchedUser) {
      return (
        fetchedUser.displayName ||
        fetchedUser.displayName ||
        `User #${fetchedUser.id}`
      )
    }
    if (user?.createdBy) return user.createdBy // fallback (in case fetch fails)
    return `User #${userId || 'N/A'}`
  }, [fetchedUser, loading, userId])

  return (
    <div className={styles['user-row']}>
      {user && user.userType && (
        <div className={styles.icon}>
          <IconPickerItem
            icon={user.userType.icon}
            size={20}
            color={user.userType.color}
          />
        </div>
      )}
      <div onClick={() => openDetails(user)} style={{ cursor: 'pointer' }}>
        {displayName}
      </div>
      {timestamp && (
        <div className={styles.timestamp}>
          <DateTime value={timestamp} />
        </div>
      )}
    </div>
  )
}

function SetCaseNumberForm({ incident, onClose, onCaseNumberAdded }) {
  const [getFieldProps, formData] = useForm()
  const [submitting, setSubmitting] = useState(false)
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault()
      setSubmitting(true)
      const { caseNumber } = formData

      try {
        if (!incident?.id) throw new Error('Missing incident ID')
        if (!caseNumber?.trim()) throw new Error('Case number is empty')

        // fallback to empty array if customData is undefined
        const existing = Array.isArray(incident.customData)
          ? incident.customData.find(
            (item) => item.type?.typeName === 'caseNumber'
          )
          : null

        if (existing) {
          alert('A case number already exists.')
          onClose(false)
          return
        }

        const incidentRef = doc(db, 'incidents', incident.id)
        await updateDoc(incidentRef, {
          customData: arrayUnion({
            type: { typeName: 'caseNumber' },
            value: caseNumber.trim()
          })
        })

        if (onCaseNumberAdded) {
          await onCaseNumberAdded()
        }

        onClose(true)
      } catch (err) {
        console.error('Error saving caseNumber to Firestore:', err)
        alert(`Error: ${err.message || err}`)
        onClose(false)
      } finally {
        setSubmitting(false)
      }
    },
    [formData, onClose, incident, onCaseNumberAdded]
  )

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        label='Please enter case number if applicable'
        name='caseNumber'
        placeholder='Case Number'
        maxLength={30}
        required={true}
        {...getFieldProps()}
      />
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <Button
          type='button'
          className='is-secondary is-disabled'
          onClick={() => onClose(true)}>
          Not applicable
        </Button>
        <div style={{ marginLeft: 'auto' }}>
          <Button type='submit'>Submit</Button>
        </div>
      </div>
    </form>
  )
}

export default function IncidentDetailsPanel({
  incident,
  dispatchBindings,
  openSecondaryPanel,
  profile,
  setCustomActiveChannel,
  setActivePanel,
  setDirectionsDestination,
  userTypes
}) {
  const addNotification = useNotifications()
  const { incidents } = useDispatchData()
  const [caseNumberModalActive, setCaseNumberModalActive] = useState(false)
  const [incidentData, reloadIncident] = useApiRequest(
    `incidents/${incident.id}`
  )
  const [incidentDataLocal, setIncidentDataLocal] = useState(incident || {})
  const { focusOnLocation } = useMap()


  useEffect(() => {
    const incidentRef = doc(db, 'incidents', incident.id)
    const unsubscribe = onSnapshot(
      incidentRef,
      (doc) => {
        setIncidentDataLocal(doc.data() || {})
      },
      (error) => {
        console.error('Error listening to incident:', error)
      }
    )

    return () => unsubscribe()
  }, [incident.id])

  const currentDispatch = useMemo(() => {
    if (!incidentDataLocal?.acceptedBy || !profile) return null

    // Check if user is in acceptedBy array
    return incidentDataLocal.acceptedBy.find(
      (entry) => entry.userId === profile.id
    )
  }, [incidentDataLocal, profile])

  const isUserResponding = useMemo(() => {
    return !!currentDispatch
  }, [currentDispatch])

  const isCreatedByUser = useMemo(() => {
    return incidentDataLocal.createdBy === profile.id
  }, [incidentDataLocal, profile])

  const addDispatchOrganization = useCallback(
    () =>
      openSecondaryPanel(
        <AddDispatchOrganization
          incident={incidentDataLocal}
          dispatchBindings={dispatchBindings}
          onDispatchOrganizationCreated={() => openSecondaryPanel(null)}
          userTypes={userTypes}
          profile={profile}
        />,
        'Add responders'
      ),
    [
      openSecondaryPanel,
      dispatchBindings,
      incidentDataLocal,
      userTypes,
      profile
    ]
  )

  const setActiveDispatch = useCallback(async () => {
    try {
      const userId = profile.id
      const now = new Date().toISOString()
      const displayName =
        profile?.displayName ||
        profile.personalData[0].value ||
        `User #${profile.id}`
      const profilePic = getUserAvatar(profile);
      const chatId = incident.chatId || `incident_${incident.id}`
      const incidentRef = doc(db, 'incidents', incident.id)
      const chatRef = doc(db, 'chats', chatId)

      // Check if chat already exists
      const chatSnap = await getDoc(chatRef)
      const incidentSnap = await getDoc(incidentRef)
      const incidentData = incidentSnap.data()

      // T30: this user's own stale entries, so they can be removed by exact value.
      // Removing by value only ever touches these elements -- it cannot discard a
      // concurrent accept written by mobile, which the old whole-array rewrite did.
      const ownLeftEntries =
        incidentData.leavedBy?.filter((entry) => entry.userId === userId) || []

      const isFirstAcceptance = !incidentData.acceptedBy?.length

      // Add new accepted entry
      // await updateDoc(incidentRef, {
      //   acceptedBy: [...updatedAcceptedBy, {
      //     accepted: true,
      //     userId,
      //     timestamp: now
      //   }],
      //   ...(!incidentData.acceptedBy?.some(e => e.accepted) && { status: 'in-progress' })
      // });

      // T30: field-level atomic operations, matching what mobile already does at
      // incident_bloc.dart:609. A read-modify-write of the whole array silently
      // dropped anyone who accepted between the getDoc above and this update.
      const updateData = {
        acceptedBy: arrayUnion({ userId, timestamp: now }),
        notificationUserList: arrayRemove(userId),
        ...(ownLeftEntries.length > 0 && {
          leavedBy: arrayRemove(...ownLeftEntries)
        }),
        ...( (isFirstAcceptance || incidentData.status === 'ended') && { status: 'inprogress' })
      }

      if (isFirstAcceptance) {
        updateData.chatId = chatId
        await Promise.all([
          updateDoc(incidentRef, updateData),
          setDoc(chatRef, {
            chatType: 'incident',
            createdAt: new Date().toISOString(),
            createdBy: [userId],
            groupName: `${incident.name ?? 'Unknown'
              } (Incident #${incident.id.slice(0, 3)})`,
            id: chatId,
            incidentId: incident.id,
            lastMessage: null,
            lastMessageId: null,
            lastMessageSenderId: null,
            lastUpdatedAt: new Date().toISOString(),
            members: [
              {
                userId: userId,
                displayName,
                profilePic,
                addedBy: null,
                isOnline: false
              }
            ],
            memberIds: [userId],
            mutedBy: [],
            pinnedMessages: [],
            profileBgColor: '#ffbb9bc3',
            typingStatus: {}
          })
        ])
      } else {
        // For subsequent acceptances, add user to existing chat if not already a member
        await updateDoc(incidentRef, updateData)

        const chatSnap = await getDoc(chatRef)
        if (chatSnap.exists()) {
          const chatData = chatSnap.data()
          // memberIds, not members: memberIds is the field the chats rule
          // reads, so it is the one that decides whether this write is allowed.
          const isMember =
            chatData.memberIds?.includes(userId) ??
            chatData.members?.some((member) => member.userId === userId)

          // Restoring an ended incident's chat is folded INTO the join rather
          // than done first. A non-member may only write this chat as part of
          // adding themselves -- an `isDeleted: false` on its own touches no
          // membership and is refused, which used to strand anyone re-accepting
          // an ended incident they had not previously joined.
          const restore = incidentData.status === 'ended' ? { isDeleted: false } : {}

          if (!isMember) {
            await updateDoc(chatRef, {
              members: arrayUnion({
                userId: userId,
                displayName,
                profilePic,
                addedBy: null,
                isOnline: false
              }),
              memberIds: arrayUnion(userId),
              lastUpdatedAt: new Date().toISOString(),
              ...restore
            })
          } else if (incidentData.status === 'ended') {
            // Already a member, so an ordinary update is allowed.
            await updateDoc(chatRef, restore)
          }
        }
      }

      await reloadIncident()
    } catch (err) {
      console.error('Failed to accept dispatch:', err)
    }
  }, [incident.id, profile, reloadIncident])

  const cancelActiveDispatch = useCallback(async () => {
    try {
      const userId = profile.id
      const now = new Date().toISOString()

      const incidentRef = doc(db, 'incidents', incident.id)
      const incidentSnap = await getDoc(incidentRef)
      const incidentData = incidentSnap.data()

      // T30: this user's own accepted entries, removed by exact value -- see
      // setActiveDispatch. Each path cleans the other array: accepting clears
      // leavedBy, leaving clears acceptedBy. A single field cannot be both
      // arrayUnion'd and arrayRemove'd in one update.
      const ownAcceptedEntries =
        incidentData.acceptedBy?.filter((entry) => entry.userId === userId) || []

      // Add new declined entry
      await updateDoc(incidentRef, {
        leavedBy: arrayUnion({ userId, timestamp: now }),
        notificationUserList: arrayRemove(userId),
        ...(ownAcceptedEntries.length > 0 && {
          acceptedBy: arrayRemove(...ownAcceptedEntries)
        })
      })

      await reloadIncident()
    } catch (err) {
      console.error('Failed to cancel dispatch:', err)
    }
  }, [incident.id, profile, reloadIncident])

  const endIncident = useCallback(async () => {
    try {
      const incidentRef = doc(db, 'incidents', incident.id)

      await updateDoc(incidentRef, {
        status: 'ended',
        endedAt: new Date().toISOString()
      })
    } catch (err) {
      console.error('Failed to update status:', err.message)
      addNotification(
        <div>
          <div>Failed to set incident status to "ended"!</div>
          <div>{err.message || 'Check console for details.'}</div>
        </div>,
        { className: 'is-danger' }
      )
      return
    }

    // Hiding the chat is best-effort, and it has to be OUTSIDE the try above.
    //
    // The chats rule requires MEMBERSHIP (uid in memberIds), and ending an
    // incident does not imply it: a creator who never accepted is not in the
    // chat. Stage incident f556Ga60 is the case -- ended from the web app by
    // its creator, status written, chat write refused. Because both writes
    // shared one try, the refusal surfaced as
    // 'Failed to set incident status to "ended"! Missing or insufficient
    // permissions' for an incident that HAD ended, and it swallowed the
    // end-incident push as well.
    //
    // The mobile app already treats this write as best-effort --
    // ChatsRepository.hideChat logs and returns false, and _endIncidentEvent
    // ignores the result -- which is why the same denial is invisible there.
    const chatId =
      incidentDataLocal?.chatId || incident.chatId || `incident_${incident.id}`

    try {
      await updateDoc(doc(db, 'chats', chatId), { isDeleted: true })
    } catch (chatError) {
      console.error('Failed to hide incident chat:', chatError.message)
    }

    try {
      const endIncidentNotification = {
        topic: `${process.env.REACT_APP_ENV === 'production' ? 'p' : ''
          }livealarm-incident-${incident.id}`,
        title: 'Incident Ended',
        body: `Incident "${incidentDataLocal.name}" has been ended by ${profile.displayName ||
          profile.personalData[0].value ||
          `User #${profile.id}`
          }`,
        data: {
          type: 'end_incident',
          incidentId: incident.id
        }
      }

      await sendNotificationToTopic(endIncidentNotification)
    } catch (notificationError) {
      console.error(
        'Failed to send end incident notification:',
        notificationError
      )
      // Don't fail the operation if notification fails
      addNotification(
        <div>
          <div>Incident ended but notification failed to send</div>
        </div>,
        { className: 'is-warning', autoDismiss: true }
      )
    }

    addNotification(
      <div>
        <div>Incident status set to "ended".</div>
      </div>,
      { className: 'is-success' }
    )
  }, [incident, incidentDataLocal, profile, addNotification])

  const handleCaseNumberAdded = useCallback(async () => {
    // This function is called after the case number is successfully added
    await endIncident()
  }, [endIncident])

  const address = incidentDataLocal.locations?.find(
    (loc) => loc.format === 'us-address'
  )
  const loc = incidentDataLocal.locations?.find(
    (loc) => loc.format === 'lat-long'
  )

  const openUserDetails = useCallback(
    (user) =>
      openSecondaryPanel(
        <UserDetails id={user.createdBy || user.userId} />,
        user.createdBy || user.user || `User #${user.userId}`
      ),
    [openSecondaryPanel]
  )

  const respondingUnits = useMemo(() => {
    return incidentDataLocal?.acceptedBy || []
  }, [incidentDataLocal])

  const openChat = useCallback(() => {
    setActivePanel('chat')
    setCustomActiveChannel(incidentDataLocal?.chatId)
  }, [setActivePanel, setCustomActiveChannel, incidentDataLocal])

  return (
    <>
      {caseNumberModalActive && (
        <ModalDialog
          title='Case Number'
          active={true}
          onClose={() => setCaseNumberModalActive(false)}>
          <SetCaseNumberForm
            incident={incidentDataLocal}
            onClose={(success) => {
              setCaseNumberModalActive(false)

              if (success) {
                endIncident()
              }
            }}
            onCaseNumberAdded={handleCaseNumberAdded}
          />
        </ModalDialog>
      )}
      <div style={{ width: '100%', position: 'relative' }}>
        <div
          className='incident-details justify-between'
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '1rem'
          }}>
          <div style={{ fontSize: '1.2rem' }}>Incident Details</div>
          {(isUserResponding || isCreatedByUser) && (
            <div style={{ marginLeft: 'auto' }}>
              <DropdownMenu label={<i className='fa fa-cog'></i>}>
                <DropdownItem onClick={() => addDispatchOrganization()}>
                  Add responders
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    const caseNumber = incidentDataLocal.customData?.find(
                      (item) => item?.type?.typeName === 'caseNumber'
                    )

                    if (!caseNumber) {
                      setCaseNumberModalActive(true)
                    } else {
                      endIncident()
                    }
                  }}>
                  End incident
                </DropdownItem>
              </DropdownMenu>
            </div>
          )}
          {/* {!isCreatedByUser && ( */}
            <CircleX
              style={{ cursor: 'pointer' }}
              onClick={() => openSecondaryPanel(null)}
            />
          {/* )} */}
        </div>
        <div className='flex flex-col pt-0 gap-4' style={{ padding: '1rem' }}>
          {incidentDataLocal.name && (
            <Info label='Name'>{incidentDataLocal.name}</Info>
          )}
          {incidentDataLocal.description && (
            <Info label='Description'>{incidentDataLocal.description}</Info>
          )}
          {address && <Info label='Address'>{formatAddress(address)}</Info>}
          {loc && (
            <Info label='Location'>
              {loc.latitude.toFixed(8)}, {loc.longitude.toFixed(8)} (Lat., Lon.)
            </Info>
          )}
        </div>
        {loc && (
          <div
            style={{
              marginTop: '1rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
            <GoogleMaps
              center={{ lng: loc.longitude, lat: loc.latitude }}
              zoom={17}
              containerStyle={{
                width: '95%',
                height: '250px',
                borderRadius: '16px',
                border: '1px solid #e7e7e7'
              }}
              // onMapClick={() => {
              //   // ✅ Opens Google Maps with a pin on this location
              //   const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`
              //   window.open(googleMapsUrl, '_blank')
              // }}
              >
              <Marker position={{ lng: loc.longitude, lat: loc.latitude }}
              onClick={() => {
          focusOnLocation(loc.latitude, loc.longitude, 17);openSecondaryPanel(false)
        }}
         />
            </GoogleMaps>
          </div>
        )}
        <div className='flex flex-col' style={{ padding: '1rem' }}>
          {isUserResponding && (
            <div className={styles['open-chat']} onClick={openChat}>
              <i className='fa fa-comments' /> Open chat
            </div>
          )}
        </div>
        <div className='flex flex-col pt-0 gap-4' style={{ padding: '1rem' }}>
          <Info label='Created by'>
            <UserRow
              user={incidentDataLocal}
              timestamp={incidentDataLocal.createdAt}
              openDetails={openUserDetails}
            />
          </Info>
          {respondingUnits.length !== 0 && (
            <Info label='Responding units'>
              <div>
                {respondingUnits.map((user, index) => (
                  <UserRow
                    key={index}
                    user={user}
                    timestamp={user.timestamp}
                    openDetails={openUserDetails}
                  />
                ))}
              </div>
            </Info>
          )}
          <Info label='Incident ID'>{incidentDataLocal.id}</Info>
        </div>
        <div className={`${styles.actions}`}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%'
            }}>
            <Button
              style={{ width: '100%', textTransform: 'uppercase' }}
              onClick={() => setActiveDispatch()}
              icon={isUserResponding && 'fa fa-check'}
              disabled={isUserResponding}>
              {isUserResponding ? 'Accepted' : 'Accept'}
            </Button>
            <Button
              className='is-danger'
              style={{ width: '100%', textTransform: 'uppercase' }}
              onClick={() => cancelActiveDispatch()}
              disabled={!isUserResponding}>
              Leave
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
